// Lib
import { round } from 'lodash';
import type React from 'react';

// Utils
import { DOM_POINTER_TYPES } from '../../../utils/domConstants';

type DrawingEditorEscapeExit = {
    event: Pick<Event, 'preventDefault' | 'stopPropagation'>;
    saveAndExit: () => void;
    discardAndExit?: () => void;
};

/**
 * Exit the drawing editor in response to Escape, discarding when the editor supports it.
 *
 * Discarding swallows the event so a surrounding modal's own Escape binding can't save on the way out.
 * Editors with no discard handler keep the legacy save-and-exit behaviour and leave the event alone.
 */
export const exitDrawingEditorOnEscape = ({ event, saveAndExit, discardAndExit }: DrawingEditorEscapeExit): void => {
    if (!discardAndExit) return saveAndExit();

    event.preventDefault();
    event.stopPropagation();
    discardAndExit();
};

/**
 * PRESSURE:
 *  Firefox
 *  Chrome  * 0 when hovering
 *          * 0.5 when drawing
 *  Safari  * 0 always
 *  iPad    * 0 when using a finger (pointerType = 'touch')
 *          * 0 -> 1 when using Apple Pencil (pointerType = 'pen')
 */
export const getPointerEventPressure = (event: PointerEvent): number =>
    event.pointerType !== DOM_POINTER_TYPES.PEN ? 0 : round(event.pressure, 2);

type CanvasPointInput = {
    pointX: number;
    pointY: number;
    pressure?: number;
    drawingEditorClientRect: DOMRect;
    canvasScale?: number;
    viewBoxX?: number;
    viewBoxY?: number;
    zoomScale?: number;
};

const getRectOriginX = (rect: DOMRect): number => (typeof rect.x === 'number' ? rect.x : rect.left || 0);
const getRectOriginY = (rect: DOMRect): number => (typeof rect.y === 'number' ? rect.y : rect.top || 0);

export const getCanvasPointFromAbsolutePoint = ({
    pointX,
    pointY,
    pressure = 0,
    drawingEditorClientRect,
    canvasScale = 1,
    viewBoxX = 0,
    viewBoxY = 0,
    zoomScale = 1,
}: CanvasPointInput): [number, number, number] => [
    round((pointX - getRectOriginX(drawingEditorClientRect)) / canvasScale / zoomScale + viewBoxX, 1),
    round((pointY - getRectOriginY(drawingEditorClientRect)) / canvasScale / zoomScale + viewBoxY, 1),
    pressure,
];

export const getCanvasPointFromClientPoint = ({
    clientX,
    clientY,
    pressure = 0,
    drawingEditorClientRect,
    canvasScale = 1,
    viewBoxX = 0,
    viewBoxY = 0,
    zoomScale = 1,
}: {
    clientX: number;
    clientY: number;
    pressure?: number;
    drawingEditorClientRect: DOMRect;
    canvasScale?: number;
    viewBoxX?: number;
    viewBoxY?: number;
    zoomScale?: number;
}): [number, number, number] =>
    getCanvasPointFromAbsolutePoint({
        pointX: clientX,
        pointY: clientY,
        pressure,
        drawingEditorClientRect,
        canvasScale,
        viewBoxX,
        viewBoxY,
        zoomScale,
    });

/**
 * Returns a vector of [x, y, pressure] for the specified pointer event.
 * Note: Pressure will be 0 unless a 'pen' pointerType is used.
 */
export const getPointerEventDetail = (
    event: PointerEvent,
    svgRef: React.MutableRefObject<HTMLElement>,
    canvasScale = 1,
    viewBoxX = 0,
    viewBoxY = 0,
    zoomScale = 1,
): [number, number, number] => {
    const drawingEditorClientRect = svgRef.current?.getBoundingClientRect() || new DOMRect(0, 0, 0, 0);
    return getCanvasPointFromAbsolutePoint({
        pointX: event.pageX,
        pointY: event.pageY,
        pressure: getPointerEventPressure(event),
        drawingEditorClientRect,
        canvasScale,
        viewBoxX,
        viewBoxY,
        zoomScale,
    });
};
